import { CameraIcon, ScanLineIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useBarcodeCamera } from "@/features/scan/use-barcode-camera"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

/**
 * Scanning a printed document (§9 amendment, §11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO INPUTS, AND THE KEYBOARD ONE IS PRIMARY.
 *
 * A handheld USB barcode scanner presents itself to the operating system as a
 * KEYBOARD: it types the decoded characters and sends Enter. So a focused text
 * input IS a scanner on the office desktop — no camera, no permission prompt, no
 * decoding, and it works with any scanner anyone plugs in. That is also the
 * manual path when someone simply reads the number off the page, and the
 * fallback on a browser with no `BarcodeDetector`.
 *
 * The camera is offered as WELL, for the phone — including the owner's iPhone,
 * since `use-barcode-camera` gained a lazily-downloaded decoder (2026-08-17).
 * It is offered wherever a camera can be OPENED; whether a decoder is ready is a
 * separate, asynchronous question the hook answers with `preparing` and
 * `error: "decoder"`. The video element is rendered only once BOTH are true, so
 * the failure this file has always warned about — a live camera that silently
 * never resolves, which looks like it is working — still cannot happen.
 *
 * WHAT COMES OFF A SCAN IS NOT ALWAYS A BARE NUMBER. Our QR carries
 * `26-27/69|2026-08-06|GSTIN|GSTIN|12,980.00`; our Code 128 carries just
 * `26-27/69`; a customer's own label carries whatever they chose. So the value
 * is normalised here — first pipe-delimited field, trimmed — and the caller
 * receives something it can look up.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * `"26-27/69|2026-08-06|…"` → `"26-27/69"`.
 *
 * Exported because it is the part worth testing: the same physical document
 * scanned as a QR and as a barcode must resolve to the same string, or "find
 * this document" works from one code and not the other.
 */
export function normaliseScan(raw: string): string {
  return (raw.split("|")[0] ?? "").trim()
}

export function ScanDialog({
  open,
  onOpenChange,
  onScan,
  title,
  description,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Receives the normalised value — a document number, usually. */
  onScan: (value: string) => void
  title?: string
  description?: string
}): React.JSX.Element {
  const { t } = useTranslation()
  const [typed, setTyped] = useState("")
  const inputRef = useRef<HTMLInputElement | null>(null)

  const camera = useBarcodeCamera((value) => {
    const normalised = normaliseScan(value)
    if (normalised === "") return
    onScan(normalised)
    onOpenChange(false)
  })

  // Reset between openings — a stale number from last time, sitting in the box
  // when the dialog reopens, is one Enter away from opening the wrong document.
  useEffect(() => {
    if (open) {
      setTyped("")
      /*
       * ═══════════════════════════════════════════════════════════════════════
       * AUTO-FOCUS ONLY WHERE A HANDHELD SCANNER MIGHT BE — I.E. NOT ON A PHONE.
       *
       * The reason for focusing at all is desktop-only: a USB barcode scanner is
       * a KEYBOARD, it types into whatever holds focus, so the field must have it
       * the moment the dialog appears. There is no USB scanner on a phone.
       *
       * On iOS this focus was reported as "the dialog opens and closes"
       * (2026-08-17, iPhone 14 Pro Max, Chrome — which is WebKit, so Safari
       * behaves the same). A bare 50ms timer races the dialog's own mount and
       * focus-trap setup, and programmatic focus on a text input ALSO raises the
       * on-screen keyboard, which resizes the visual viewport mid-animation.
       * Focus landing outside the trap, or that resize read as an outside
       * interaction, dismisses the dialog.
       *
       * `(pointer: fine)` is the honest question — "is this a mouse-and-keyboard
       * machine?" — rather than a width breakpoint, which would answer wrongly
       * for a tablet with a keyboard or a small laptop window.
       *
       * Suppressing it on touch is also the BETTER BEHAVIOUR independent of the
       * bug: raising a keyboard over the camera view is the opposite of what
       * somebody pointing a phone at a printed challan wants. He can still tap
       * the box and type; nothing is unreachable.
       * ═══════════════════════════════════════════════════════════════════════
       */
      const hasFinePointer =
        typeof window.matchMedia === "function" && window.matchMedia("(pointer: fine)").matches
      if (hasFinePointer) {
        window.setTimeout(() => inputRef.current?.focus(), 50)
      }
    } else {
      camera.stop()
    }
    // `camera` is stable enough for this; re-running on every render would tear
    // the camera down mid-scan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const submit = () => {
    const value = normaliseScan(typed)
    if (value === "") return
    onScan(value)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? t("scan.title")}</DialogTitle>
          <DialogDescription>{description ?? t("scan.help")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* ── The keyboard path, always present ────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="scan-input">{t("scan.inputLabel")}</Label>
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                id="scan-input"
                className="tabular-nums"
                placeholder={t("scan.inputPlaceholder")}
                value={typed}
                onChange={(event) => {
                  setTyped(event.target.value)
                }}
                onKeyDown={(event) => {
                  // A USB scanner ends its transmission with Enter. So does a
                  // person typing. Same handler.
                  if (event.key === "Enter") {
                    event.preventDefault()
                    submit()
                  }
                }}
              />
              <Button type="button" onClick={submit} disabled={normaliseScan(typed) === ""}>
                {t("btn.open")}
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">{t("scan.inputHelp")}</p>
          </div>

          {/* ── The camera path, only where a camera can be opened ────────── */}
          {camera.supported ? (
            <div className="flex flex-col gap-2">
              {/*
                * ═════════════════════════════════════════════════════════════
                * MOUNTED ALWAYS, SHOWN ONLY WHEN RUNNING — and that is a FIX,
                * not a style choice.
                *
                * This element used to be rendered only in the `running` branch,
                * which made the camera path unreachable on EVERY device: the
                * hook needs `videoRef.current` to attach the stream, and it can
                * only set `running` after that has worked. The element was
                * therefore never in the DOM at the moment it was needed,
                * `start()` took its `video === null` bail-out, and the button
                * simply did nothing. Nothing logged it, because giving up
                * quietly is what that branch is for.
                *
                * Present-but-hidden costs an empty `<video>` in the dialog and
                * removes the ordering problem entirely.
                *
                * HIDDEN WITH `sr-only`, NEVER WITH `hidden`. `hidden` is
                * `display: none`, and WebKit REFUSES to play a media element
                * that is not rendered. `running` is only set AFTER `play()`
                * resolves, so `hidden` gated visibility on the very call that
                * needs visibility to succeed: on the owner's iPhone `play()`
                * rejected, the hook's outer catch reported `error: "camera"`,
                * and he was told the camera could not be used on a phone whose
                * camera is perfectly fine — the wrong cause, for the one device
                * this whole path exists for. `sr-only` keeps the element in the
                * render tree (clipped to a pixel, off in a corner), which is all
                * WebKit asks. Decoding is unaffected: `detect()` reads the
                * frame's intrinsic size, not the CSS box.
                *
                * `playsInline` matters on iOS, which otherwise takes video
                * fullscreen and hides the dialog behind it.
                * ═════════════════════════════════════════════════════════════
                */}
              <video
                ref={camera.videoRef}
                className={
                  camera.running
                    ? "bg-muted aspect-video w-full rounded-md object-cover"
                    : "sr-only"
                }
                muted
                playsInline
              />
              {camera.running ? null : (
                <Button
                  type="button"
                  variant="outline"
                  /*
                   * DISABLED WHILE PREPARING, with the label saying so. The
                   * first tap on an iPhone downloads a decoder over workshop
                   * mobile data, which is not instant — a button that looked
                   * idle would be tapped again, and again.
                   */
                  disabled={camera.preparing}
                  onClick={() => {
                    void camera.start()
                  }}
                >
                  <CameraIcon />
                  {camera.preparing ? t("scan.preparing") : t("scan.useCamera")}
                </Button>
              )}

              {camera.error === null ? null : (
                <Alert>
                  {/* Declining the permission is a decision, not a fault; a
                    * decoder that would not download is a different problem with
                    * a different fix. Either way the box above still works, so
                    * both are notes rather than dead ends. */}
                  <AlertDescription>
                    {camera.error === "decoder"
                      ? t("scan.decoderUnavailable")
                      : t("scan.cameraUnavailable")}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground flex items-start gap-2 text-xs">
              <ScanLineIcon aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              {t("scan.noCameraHere")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
